import React, { useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Box,
  Chip,
  Fade,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import ProjectDescription from "../components/ProjectDescription";

// Mock data with categories
const projects = [
  {
    titleKey: "seafood_restaurant_title",
    descKey: "seafood_restaurant_desc",
    image: "/portfolio/nostimi-thalassa.png",
    link: "https://nostimi-thalassa.gr/",
    category: "website",
  },
  {
    titleKey: "olive_business_title",
    descKey: "olive_business_desc",
    image: "/portfolio/tsipelisfoods.png",
    link: "https://tsipelisfoods.com/",
    category: "website",
  },
  {
    titleKey: "earthworks_title",
    descKey: "earthworks_desc",
    image: "/portfolio/earthwork.png",
    link: "https://earthwork.gr/",
    category: "website",
  },
  {
    titleKey: "beach_studios_title",
    descKey: "beach_studios_desc",
    image: "/portfolio/amagio.png",
    link: "https://amagio.gr/",
    category: "website",
  },
  {
    titleKey: "brainstormvisions_title",
    descKey: "brainstormvisions_desc",
    image: "/portfolio/brainstormvisions.png",
    link: "#",
    category: "website",
  },
  {
    titleKey: "real_estate_title",
    descKey: "real_estate_desc",
    image: "/portfolio/ritasproperties.png",
    link: "https://ritasproperties.com/",
    category: "website",
  },
  {
    titleKey: "beach_bar_title",
    descKey: "beach_bar_desc",
    image: "/portfolio/africabeachbar.png",
    link: "https://africabeachbar.gr/",
    category: "website",
  },
  {
    titleKey: "bino_title",
    descKey: "bino_desc",
    image: "/portfolio/bino.png",
    link: "https://bino.gr",
    category: "webapp",
  },
  {
    titleKey: "incooking_title",
    descKey: "incooking_desc",
    image: "/portfolio/incooking.png",
    link: "https://incooking.eu/",
    category: "webapp",
  },
  {
    titleKey: "psychotest_title",
    descKey: "psychotest_desc",
    image: "/portfolio/psychotest.png",
    link: "https://psychotest.brainstorm-visions.com/",
    category: "webapp",
  },
  {
    titleKey: "groomer_shop_title",
    descKey: "groomer_shop_desc",
    image: "/portfolio/papel-groomers.png",
    link: "https://pupelle.eu/", // βάλε το πραγματικό URL
    category: "eshop",
  },
  {
    titleKey: "maisonette_site_title",
    descKey: "maisonette_site_desc",
    image: "/portfolio/vasias-maisonette.png",
    link: "https://vasias-maisonette.gr/", // βάλε το πραγματικό URL
    category: "website",
  },
];

const categories = [
  { key: "all", labelKey: "category_all" },
  { key: "website", labelKey: "category_website" },
  { key: "eshop", labelKey: "category_eshop" },
  { key: "webapp", labelKey: "category_webapp" },
  { key: "mobile", labelKey: "category_mobile" },
];

const categoryColors = {
  website: "primary",
  eshop: "success",
  webapp: "warning",
  mobile: "secondary",
};

const Portfolio = () => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState("all");

  const filteredProjects =
    selected === "all" ? projects : projects.filter((p) => p.category === selected);

  return (
    <>
      <Helmet>
        <title>{t("meta_title_portfolio")}</title>
        <meta name="description" content={t("meta_description")} />
        <link rel="alternate" hrefLang="en" href="brainstorm-visions.com/en" />
        <link rel="alternate" hrefLang="el" href="brainstorm-visions.com/el" />
      </Helmet>

      <Container sx={{ py: { xs: 4, md: 8 } }}>
        <Typography variant="h3" fontWeight="bold" mb={4}>
          {t("portfolio")}
        </Typography>
        {/* Φίλτρα */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 5,
            flexWrap: "wrap",
            position: "sticky",
            top: 64,
            zIndex: 10,
            background: "rgba(255,255,255,0.8)",
            backdropFilter: "blur(8px)",
            p: 2,
            borderRadius: 2,
            boxShadow: 2,
          }}
        >
          {categories.map((cat) => (
            <Button
              key={cat.key}
              variant={selected === cat.key ? "contained" : "outlined"}
              color={selected === cat.key ? "primary" : "inherit"}
              onClick={() => setSelected(cat.key)}
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                fontSize: "1rem",
                borderRadius: 5,
                boxShadow: selected === cat.key ? 4 : 0,
                transition: "0.2s",
              }}
              component={motion.button}
              whileTap={{ scale: 0.97 }}
              whileHover={{ scale: 1.06 }}
            >
              {t(cat.labelKey)}
            </Button>
          ))}
        </Box>
        {/* Έργα */}
        <Grid container spacing={4}>
          {filteredProjects.length === 0 ? (
            <Grid item xs={12}>
              <Fade in>
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {t("no_projects")}
                  </Typography>
                  <img src={process.env.PUBLIC_URL + "/empty-state.svg"} alt="No projects" width={220} style={{ opacity: 0.7 }} />
                </Box>
              </Fade>
            </Grid>
          ) : (
            filteredProjects.map((p, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <motion.div
                  whileHover={{ y: -8, boxShadow: "0 8px 32px rgba(0,0,0,0.10)" }}
                  transition={{ type: "spring", stiffness: 220 }}
                  style={{ height: "100%" }}
                >
                  <Card
                    sx={{
                      boxShadow: 5,
                      borderRadius: 4,
                      transition: "0.3s",
                      height: "100%",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    {/* Κατηγορία badge */}
                    <Chip
                      label={t(categories.find((c) => c.key === p.category)?.labelKey)}
                      color={categoryColors[p.category] || "default"}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 18,
                        left: 18,
                        zIndex: 2,
                        fontWeight: "bold",
                        background: "rgba(255,255,255,0.9)",
                        color: "#222",
                        letterSpacing: "0.5px"
                      }}
                    />
                    <CardMedia
                      component="img"
                      height="210"
                      image={p.image}
                      alt={t(p.titleKey)}
                      sx={{
                        filter: "brightness(0.92)",
                        transition: "0.2s",
                        ":hover": { filter: "brightness(1)" },
                      }}
                    />
                    <CardContent sx={{ pb: 2 }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {t(p.titleKey)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        <ProjectDescription descKey={p.descKey} />
                      </Typography>
                      {p.link && (
                        <Button
                          variant="outlined"
                          href={p.link}
                          target="_blank"
                          sx={{ mt: 1, borderRadius: 5 }}
                        >
                          {t("see_more")}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))
          )}
        </Grid>
      </Container>
    </>
  );
};

export default Portfolio;